<template>
  <ContainerComponent class="view-container">
    <div class="tile sm-12">
      <div class="header">
        <h1 class="text-lg title">Custom selection of products</h1>
      </div>
      <div class="row">
        <div class="column">
          <div class="row sm-12 text-input-row">
            <div class="column md-6 lg-4">
              <TextInput
                id="input-top"
                v-model="enteredName"
                :value="enteredName"
                :isIconDisplayed="true"
                iconName="icon-crown"
                class="text-input"
                placeholder="Enter project name"
                :isMandatory="isEdit ? !isEdit : !isEnteredNameUnique"
                errorMessage="This project name is already in use. Please choose a different project name."
              />
            </div>
            <button
              class="btn primary save-btn"
              @click="onSaveAndExit"
            >
              Save and exit
            </button>
          </div>
          <div class="main-section" v-if="groups">
            <div class="row groups-aria">
              <div class="tabs">
                <div class="titles">
                  <div
                    v-for="groupName in groupNameList"
                    :key="'g:' + groupName"
                    class="title"
                    :class="getGroupSelectedClassByName(groupName)"
                    @click="onGroupSelect(groupName)"
                  >
                    {{ groupName }}
                  </div>
                </div>
              </div>
              <div class="push-right two-buttons">
                <button class="btn showcase-btn" @click="onSelectAll()">
                  Select all
                </button>
                <button class="btn showcase-btn" @click="onResetSelection()">
                  Clear
                </button>
              </div>
            </div>
            <div class="row sections" v-if="groups">
              <CheckboxGroup
                v-for="familyName in currentFamilyNameList"
                :key="currentGroup + familyName"
                ref="checkboxGroup"
                :group-name="currentGroup"
                :family-name="familyName"
                class="column checkbox-group"
                :checked="checkedFamilyProducts[currentGroup][familyName] || []"
                :radios="getFamilyRadios(familyName)"
                @checked-changed="
                  productIdList =>
                    onSelectedProductsChanged(productIdList, familyName)
                "
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </ContainerComponent>
</template>
<script>
import {
  ContainerComponent,
  CheckboxGroup,
  TextInput,
} from '@/components';
import { DEFAULT_CUSTOM_SELECTION_NAME, DEFAULT_PROJECT } from '@/constants';
import readIsManaVersionFlag from '@/shared/manaVersionHelper';
import ProjectSelectionService from '@/services/projectSelectionService';
import { getStaticUrl } from '@/helpers';
import axios from 'axios';
import { getGroupsFromSections } from '@/helpers';
import NotificationService from '@/helpers/notification';
import StaticUrlService from '@/services/staticUrlService';

export default {
  components: {
    ContainerComponent,
    TextInput,
    CheckboxGroup,
  },
  props: {
  },
  data() {
    return {
      currentGroup: '',
      enteredName: '',
      customSelectionName: '',
      checkedProducts: {},
      checkedFamilyProducts: {},
      currentSelectionId: '',
      groups: undefined
    };
  },
  created() {
    this.customSelectionProjectsKey = readIsManaVersionFlag() ? 'MANA_customSelectionProjects' : 'Global_customSelectionProjects';
  },
  computed: {
    allRadios() {
      return this.groupNameList
        .flatMap(groupName => Object.values(this.groups[groupName]))
        .flatMap(familyRadioList => familyRadioList);
    },
    allSectionNames() {
      return Array.from(
        new Set(this.allRadios.map(radio => radio.sectionName))
      );
    },
    allProjects() {
      return this.$localStorage.get(this.customSelectionProjectsKey);
    },
    allProjectsWithEditable() {
      return this.allProjects?.map((project, index) =>
        index === this.$route.params.projectNumber
          ? this.currentProject
          : project
      );
    },

    localStorageCurrentProject() {
      return this.$localStorage
        .get(this.customSelectionProjectsKey)
        .find((el, index) => +index === +this.$route.params.projectNumber);
    },
    currentProject() {
      return {
        id: this.currentSelectionId,
        name: this.enteredName,
        checkedProducts: this.checkedProducts,
        productType: ProjectSelectionService.getProductTypeName(readIsManaVersionFlag())
      };
    },

    projectsCount() {
      return this.$localStorage.get(this.customSelectionProjectsKey).length;
    },
    initialCustomSelectionName() {
      return this.isEdit
        ? this.customSelectionName
        : '';
    },

    groupNameList() {
      return Object.keys(this.groups);
    },
    currentFamilyNameList() {
      return Object.keys(this.groups[this.currentGroup] || {});
    },

    activeCategories() {
      return Object.keys(this.checkedProducts).filter(
        sectionName => this.checkedProducts[sectionName].length
      );
    },
    isCustomSelection() {
      return !!this.activeCategories.length;
    },
    isEnteredName() {
      return (
        this.enteredName.trim() !== DEFAULT_PROJECT.name &&
        !!this.enteredName.trim()
      );
    },
    isEnteredNameUnique() {
      return !this.$localStorage
        .get(this.customSelectionProjectsKey)
        .filter(el => el.name === this.enteredName.trim()).length;
    },
    isEdit() {
      return (
        this.$route.params.projectNumber === 0 ||
        !!this.$route.params.projectNumber
      );
    },
    isSaveAllowed() {
      return this.isEdit
        ? this.isCustomSelection && this.isEnteredName
        : this.isCustomSelection &&
            this.isEnteredName &&
            this.isEnteredNameUnique;
    },
    staticUrl () {
      return getStaticUrl(null, this.$route.name);
    }
  },
  async mounted() {
    const fullStaticUrl = await StaticUrlService.getFullStaticUrl(null, this.$route.name);
    axios.get(`${fullStaticUrl}productData.json?` + Date.now()).then(result => {
      this.$store.commit(
        'radios',
        result.data.filter(x => x.isMana == readIsManaVersionFlag())
      );

      this.groups = this.getGroups();

      this.redirectIfIncorrectProjectNumber();
      this.createCheckedFamilyProducts();
      this.setInitialValues();
    });
  },

  methods: {
    redirectIfIncorrectProjectNumber() {
      if (
        +this.$route.params.projectNumber < 0 ||
        +this.$route.params.projectNumber > this.allProjects.length - 1
      ) {
        this.$router.push({ name: 'custom-selection-create' });
      }
    },
    setInitialValues() {
      if (this.isEdit) {
        this.checkedProducts = this.localStorageCurrentProject.checkedProducts;
        this.customSelectionName = this.localStorageCurrentProject.name;
        this.currentSelectionId = this.localStorageCurrentProject.id;
        this.fillCheckedFamilyProducts(this.checkedProducts);
      }
      this.currentGroup = this.groupNameList[0];
      this.enteredName = this.initialCustomSelectionName;
    },

    getFamilyRadios(familyName) {
      return this.groups[this.currentGroup][familyName];
    },
    getGroupSelectedClassByName(groupName) {
      return groupName === this.currentGroup ? 'selected' : '';
    },

    addProjectToLocalStorage() {
      this.$localStorage.set(
        this.customSelectionProjectsKey,
        this.isEdit
          ? this.allProjectsWithEditable
          : [...this.allProjects, this.currentProject]
      );
    },
    saveProjectToStore() {
      this.$store.commit('customSelectionId', this.currentProject.id);
      this.$store.commit('customSelectionName', this.currentProject.name);
      this.$store.commit('customSelectionCheckedProducts', {
        ...this.currentProject.checkedProducts
      });
    },

    async onSaveAndExit() {
      if (this.isEdit) {
        if (!!this.enteredName && this.enteredName != ' ') {
          await ProjectSelectionService.update(this.currentProject, this.customSelectionProjectsKey)
          this.saveProjectToStore();
          this.$router.push({ name: 'custom-selection-projects' });
        } else {
          NotificationService.error('Please add the project name.');
        }
      } else {
        if (this.isSaveAllowed) {
          await ProjectSelectionService.create(readIsManaVersionFlag(), this.currentProject.name, this.currentProject.checkedProducts, this.customSelectionProjectsKey);
          this.saveProjectToStore();
          this.$router.push({ name: 'custom-selection-projects' });
        } else {
        if (!this.isCustomSelection || !this.isEnteredName) {
          NotificationService.error('Please add the project name and select products.');
          }
        }
      }
      
    },
    onResetSelection() {
      this.$refs.checkboxGroup?.forEach(checkbox => checkbox.reset());
    },
    onSelectedProductsChanged(checkedNames, familyName) {
      this.addToCheckedFamilyProducts(checkedNames, familyName);
      this.checkedProducts = this.getCheckedSectionProductsFromFamilyProducts();
    },
    onSelectAll() {
      this.$refs.checkboxGroup.forEach(checkbox => checkbox.selectAll());
    },
    onGroupSelect(groupName) {
      this.currentGroup = groupName;
    },

    createCheckedFamilyProducts() {
      let checkedFamilyProducts = {};
      this.groupNameList.forEach(groupName => {
        if (checkedFamilyProducts[groupName] === undefined) {
          checkedFamilyProducts[groupName] = {};
        }
        Object.keys(this.groups[groupName]).forEach(
          familyName => (checkedFamilyProducts[groupName][familyName] = [])
        );
      });
      this.checkedFamilyProducts = checkedFamilyProducts;
    },
    fillCheckedFamilyProducts(checkedProducts) {
      this.createCheckedFamilyProducts();
      Object.values(checkedProducts).forEach(sectionRadioList =>
        sectionRadioList.forEach(checkedRadioId => {
          const checkedRadio = this.allRadios.find(
            radio => checkedRadioId === radio.productId
          );
          if (!!checkedRadio?.group) {
            this.checkedFamilyProducts[checkedRadio?.group][checkedRadio?.family]?.push(checkedRadio?.productId);
          }
        })
      );
    },
    addToCheckedFamilyProducts(productIdList, familyName) {
      this.checkedFamilyProducts[this.currentGroup][familyName] = productIdList;
      this.checkedFamilyProducts = { ...this.checkedFamilyProducts };
    },
    getCheckedSectionProductsFromFamilyProducts() {
      let checkedProducts = {};
      this.allSectionNames.forEach(
        sectionName => (checkedProducts[sectionName] = [])
      );
      this.groupNameList.forEach(groupName =>
        Object.keys(this.groups[groupName]).forEach(familyName =>
          this.checkedFamilyProducts[groupName][familyName].forEach(radioId => {
            const checkedRadio = this.allRadios.find(
              radio => radio.productId === radioId
            );
            checkedProducts[checkedRadio.sectionName].push(checkedRadio.productId);
          })
        )
      );
      return checkedProducts;
    },

    getGroups() {
      const familyProducts = new Set();
      this.$localStorage.get('allFamilyPreview')
        .map((family) => family.familyProducts)
        .map((products) => products.map((prod) => prod.productId))
        .forEach((products) => {
          products.forEach((prod) => familyProducts.add(prod));
        });

      return getGroupsFromSections(this.$store.state.radios.filter(x => !x.parentId && familyProducts.has(x.productId)));
    }
  }
};
</script>

<style lang="less" scoped>
@import '~@eds/vanilla/variables/dark.less';
@import '~@eds/vanilla/variables/global.less';
@import '../assets/common.less';

.actionbars {
  display: flex;
  justify-content: flex-end;
}
.btn,
.primary {
  min-width: 116px;
  text-align: center;
}
.save-btn {
  margin-left: auto;
  height: fit-content;
}
.tile {
  flex: 1;
  position: relative;
  background-color: @gray-09;
}
.dark *::-webkit-scrollbar {
  background-color: @gray-14;
}
.space-between {
  justify-content: space-between;
}
.view-container {
  overflow: scroll;
}
.text-input-row {
  display: flex;
  padding: @space-large;
  margin-bottom: @space-base;
  background-color: @gray-14;
  max-height: 65 / @px;
}
.groups-aria {
  display: flex;
  background-color: @gray-14;
  margin-bottom: @space-large;
  .push-right {
    margin-left: auto;
  }
}
.row.sections {
  margin-bottom: @space-base;
  background-color: @gray-14;
}
.row {
  flex: 1;
  .checkbox-group {
    padding-right: @space-base;
    margin-bottom: @space-large;
  }
  .column {
    display: flex;
    flex-direction: column;
    .sections {
      margin-bottom: @space-large;
      > :not(:first-child) {
        border-left: 1px solid @gray-31;
        padding: 0 @space-base 0 @space-base;
      }
      > div {
        flex-basis: 10%;
      }
    }
  }
}

.header {
  padding: @space-base @space-large;
  .title {
    font-weight: 700;
  }
}
.main-section {
  background-color: @gray-14;
  padding: @space-large;
  flex: 1 0 auto;
}
</style>
